import React, { useRef, useState, useEffect } from 'react';
import { uniq } from 'lodash-es';
import { formatOptions } from '@gp/components/Select/utils';
import { getOptionValues } from '@gp/components/SelectMultiple/utils';
import { CONSTANT_TEXTS } from '@gp/constants/const';
import { GROUP_KEY } from '@gp/constants/select';

import type { SelectOptionWithGroup, ChangeContext } from 'tea-component';

/**
 * Strategic copy of GP useSearchSelect with selection inheritance in search mode.
 * GP clears checked state when everything was selected (`isSelectAll ? []`);
 * we keep matching selected values so search results stay checked.
 */
export default function useSearchSelectInherit(props: {
  searchMode: boolean;
  searchValue: string;
  filter: (inputValue: string, option: SelectOptionWithGroup) => boolean;
  optionTextRenderer?: IGPSelectOptionTextRenderer;
  options: IGPSelectOptionWithGroup[] | null;
  value: string[];
  setValue: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const { searchMode, searchValue, filter, optionTextRenderer, options, value, setValue } = props || {};

  const allSearchingValues = useRef<string[]>([]);

  const [searchingOptions, setSearchingOptions] = useState<IGPSelectOptionWithGroup[]>(options || []);
  const [searchingValue, setSearchingValue] = useState<string[]>(value || []);

  const isSearchingSelectAll = !!searchingOptions?.length
    && searchingValue?.length === searchingOptions?.length;

  const updateSearchState = (payload: string) => {
    if (!searchMode) return;
    let searchingOptionsTmp = options?.filter((option) => filter?.(payload, option) ?? true) || [];
    searchingOptionsTmp = formatOptions(searchingOptionsTmp, {
      searchable: true,
      searchValue: payload,
      optionTextRenderer,
    });
    setSearchingOptions(searchingOptionsTmp);

    allSearchingValues.current = getOptionValues(searchingOptionsTmp);
    const searchingValueTmp = value?.filter((item) => allSearchingValues.current?.includes(item)) || [];
    setSearchingValue(searchingValueTmp);
  };

  const handleChangeWhenSearching = (
    payload: string[],
    context: ChangeContext<React.SyntheticEvent<Element, Event>> | null,
  ) => {
    if (!searchMode) return;
    // @ts-ignore
    const { name: checkContextName, value: checkContextValue } = context?.check || {};
    if (!checkContextName) return;
    if (checkContextValue) {
      setSearchingValue([...searchingValue, checkContextName]);
      setValue([...value, checkContextName]);
      return;
    }
    setSearchingValue(searchingValue?.filter((item) => item !== checkContextName) || []);
    setValue(value?.filter((item) => item !== checkContextName) || []);
  };

  const handleSelectAll = () => {
    if (!searchMode) return;
    if (!Array.isArray(allSearchingValues.current) || !allSearchingValues.current.length) return;
    setValue(uniq([...value, ...allSearchingValues.current]));
    setSearchingValue([...allSearchingValues.current]);
  };

  const handleClearAll = () => {
    if (!searchMode) return;
    if (!Array.isArray(allSearchingValues.current) || !allSearchingValues.current.length) return;
    setValue(value?.filter((item) => !allSearchingValues.current?.includes(item)));
    setSearchingValue([]);
  };

  const searchingGroups = {
    [GROUP_KEY]: (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{CONSTANT_TEXTS.SELECT_SEPARATED_GROUP_NAME}</span>
        {searchingOptions?.length > 0 && (
          <span
            className="dbg-text-highlight dbg-cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              if (isSearchingSelectAll) {
                handleClearAll();
              } else {
                handleSelectAll();
              }
            }}
          >
            {isSearchingSelectAll ? 'Clear All' : 'Select All'}
          </span>
        )}
      </div>
    ),
  };

  useEffect(() => {
    if (!searchMode) {
      setSearchingOptions([]);
      setSearchingValue([]);
      return;
    }

    updateSearchState(searchValue);
  }, [searchMode, searchValue]);

  return {
    searchingGroups,
    searchingOptions,
    searchingValue,
    onChangeWhenSearching: handleChangeWhenSearching,
  };
}
